import { createContext, useContext, MutableRefObject, useRef } from 'react';

import { assertUtil, hookUtils } from '@pukka/common/utils';
import { proxy } from '@pukka/valtio';
import debounce from 'lodash/debounce';
import once from 'lodash/once';
import { matchSorter, MatchSorterOptions } from 'match-sorter';

interface SelectModalContext<T> {
  // _max: number;
  // _selected: Set<T>;
  min: number;
  items: Set<T>;
  selected: Set<T>;
  disabled: Set<T>;
  readonly filter: {
    items: Set<T>;
    keyword: string;
    options?: MatchSorterOptions<T>;
    isUpdating: boolean;
    readonly count: number;
  };
  readonly enabledItems: Set<T>;
  readonly count: number;
  readonly max: number;
  readonly isAllSelected: boolean;
  readonly onAdd: (...items: T[]) => void;
  readonly onRemove: (...items: T[]) => void;
  readonly onToggle: (...items: T[]) => void;
  readonly onToggleAll: () => void;
  readonly onChangeFilter: (keyword?: string) => void;
  readonly onCancel: () => void;
  readonly useUpdate: (
    // eslint-disable-next-line no-use-before-define
    initialData: Pick<InitialData<T>, 'items' | 'selected' | 'disabled' | 'maxItems' | 'minItems' | 'filterBy' | 'filterOptions'>,
  ) => void;
}

type InitialData<T> = Partial<
  {
    [key in keyof Pick<SelectModalContext<T>, 'items' | 'selected' | 'disabled'>]: SelectModalContext<T>[key] | T[];
  } & {
    maxItems: SelectModalContext<T>['max'];
    minItems: SelectModalContext<T>['min'];
    filterBy?: SelectModalContext<T>['filter']['keyword'];
    filterOptions?: SelectModalContext<T>['filter']['options'];
    resetOnClose?: boolean;
  }
>;

function sortBy<T>(items: Set<T>, searchTerm: string, options?: MatchSorterOptions<T>) {
  return matchSorter<T>([...items], searchTerm, options);
}

function createUpdateFunctions<T>(
  State: SelectModalContext<T>,
  { items, selected, disabled, minItems, maxItems, filterBy, filterOptions }: InitialData<T>,
  cache: Record<string, unknown>,
) {
  return {
    items() {
      State.items = new Set(items);
      State.onChangeFilter(State.filter.keyword);
    },
    selected() {
      cache.selected = new Set(selected);
      State.selected = new Set(selected);
    },
    disabled() {
      State.disabled = new Set(disabled);
    },
    min() {
      State.min = minItems ?? State.min;
    },
    max() {
      cache.max = maxItems ?? cache.max ?? Number.POSITIVE_INFINITY;
    },
    filterBy() {
      State.onChangeFilter(filterBy);
    },
    filterOptions() {
      State.filter.options = filterOptions;
    },
  };
}

function createUpdateFilter<T>() {
  return debounce((state: SelectModalContext<T>) => {
    state.filter.items = state.filter.keyword
      ? new Set(sortBy(state.items, state.filter.keyword, state.filter.options))
      : new Set(state.items);
    state.filter.isUpdating = false;
  });
}

const createSelectModalStateContext = once(<T>() => createContext({} as SelectModalContext<T>));
const useSelectModalStateContext = <T>() => useContext(createSelectModalStateContext<T>());

function createProxy<T>(initialData?: InitialData<T>) {
  const onUpdateFilter = createUpdateFilter<T>();
  const cache = {
    max: initialData?.maxItems ?? Number.POSITIVE_INFINITY,
    selected: new Set<T>(initialData?.selected),
  };

  const State = proxy({
    min: initialData?.minItems ?? 0,
    items: new Set<T>(initialData?.items),
    selected: new Set<T>(initialData?.selected),
    disabled: new Set<T>(initialData?.disabled),
    filter: {
      keyword: initialData?.filterBy ?? '',
      items: new Set(initialData?.items ?? []),
      isUpdating: false,
      options: initialData?.filterOptions,
      get count(): number {
        return State.filter.items.size;
      },
    },
    get enabledItems(): Set<T> {
      if (State.disabled.size > 0) {
        return new Set([...State.items].filter((i) => !State.disabled.has(i)));
      }

      return State.items;
    },
    get count(): number {
      return State.selected.size;
    },
    get max(): number {
      return Math.min(cache.max, State.items.size);
    },
    get isAllSelected(): boolean {
      return State.count >= State.max || State.count >= State.enabledItems.size;
    },
    onAdd(...items: T[]) {
      items.forEach((i) => State.selected.add(i));

      State.selected = new Set(State.selected);
    },
    onRemove(...items: T[]) {
      items.forEach((i) => State.selected.delete(i));

      State.selected = new Set(State.selected);
    },
    onToggle(...items: T[]) {
      const toggleableItems = items.filter((i) => !State.disabled.has(i));

      if (!toggleableItems.length) {
        return;
      }

      const isAllSelected = toggleableItems.every((i) => State.selected.has(i));

      if (isAllSelected) {
        State.onRemove(...toggleableItems);
      } else {
        State.onAdd(...toggleableItems);
      }
    },
    onToggleAll() {
      if (State.isAllSelected) {
        State.selected = new Set();
      } else {
        State.selected = new Set([...State.enabledItems].slice(0, State.max));
      }
    },
    onChangeFilter(keyword?: string) {
      State.filter.keyword = keyword ?? '';
      State.filter.isUpdating = true;

      onUpdateFilter(State);
    },
    onCancel() {
      if (initialData?.resetOnClose) {
        State.selected = new Set(cache.selected);
        State.onChangeFilter();
      }
    },
    useUpdate({ items, selected, disabled, minItems, maxItems, filterBy, filterOptions }) {
      const {
        items: itemsFn,
        selected: selectedFn,
        disabled: disabledFn,
        min: minFn,
        max: maxFn,
        filterBy: filterByFn,
        filterOptions: filterOptionsFn,
      } = createUpdateFunctions(State, { items, selected, disabled, minItems, maxItems, filterBy, filterOptions }, cache);

      hookUtils.useUpdate(itemsFn, [items]);
      hookUtils.useUpdate(selectedFn, [JSON.stringify([...(selected ?? [])])]);
      hookUtils.useUpdate(disabledFn, [JSON.stringify([...(disabled ?? [])])]);
      hookUtils.useUpdate(minFn, [minItems]);
      hookUtils.useUpdate(maxFn, [maxItems]);
      hookUtils.useUpdate(filterByFn, [filterBy]);
      hookUtils.useUpdate(filterOptionsFn, [filterOptions]);
    },
  } as SelectModalContext<T>);

  return State;
}

function createState<T>(ref: MutableRefObject<SelectModalContext<T> | null>, initialData?: InitialData<T>) {
  if (ref.current === null) {
    ref.current = createProxy<T>(initialData);
  }

  return ref.current;
}

function useSelectModalState<T>(initialData: InitialData<T>) {
  const state = useRef<SelectModalContext<T>>(null);

  createState<T>(state, initialData);
  assertUtil.assertRefIsNotNull(state);

  return state.current;
}

export type { SelectModalContext };

export {
  createSelectModalStateContext,
  useSelectModalStateContext,
  useSelectModalState,
  createState as _createState,
  createProxy as _createProxy,
  sortBy as _sortBy,
  createUpdateFilter as _createUpdateFilter,
  createUpdateFunctions as _createUpdateFunctions,
};
